import { CallWidgetView } from '../../../types'
import { MaximizedUserCallWidget, MinimizedUserCallWidget } from '.'
import { useEffect, useRef, useState } from 'react'
import { removeCall, upsertActiveAvailableCall } from 'store/callCenterSlice'
import axios from 'axios'
import { ConnectionState, RemoteAudioTrack, Room, RoomEvent } from 'livekit-client'
import { useDispatch } from 'react-redux'
import { ClientCall, CallStatus } from '@numeo/types'
import { addNotification } from 'store/notificationSlice'
import { Category } from 'hooks/useNotification'
import { useAuth } from 'hooks/auth'
import { useAppSelector } from 'hooks'
import { showError } from 'utils/snackbar'
import { waitFor } from 'utils/wait-for'

interface SoftPhoneCallWidgetProps {
    callId: string
    index: number
    removeFakeRingtone: () => void
}
export const SoftPhoneCallWidget = ({ callId, index, removeFakeRingtone }: SoftPhoneCallWidgetProps) => {
    const { application } = useAuth()
    const [view, setView] = useState<CallWidgetView>(CallWidgetView.MINIMIZED)
    const [isMinimizing, setIsMinimizing] = useState(false)
    const [isTransitioning, setIsTransitioning] = useState(false)
    const livekitRoom = useRef<Room | null>(null)
    const [muted, setMuted] = useState(false)
    const [endingCall, setEndingCall] = useState(false)
    const dispatch = useDispatch()
    const { activeAvailableCalls } = useAppSelector((state) => state.callCenter)
    const activeAvailableCall = activeAvailableCalls[callId]
    const activeAvailableCallRef = useRef(activeAvailableCall)
    const callInitialized = useRef(false)
    const initializationFailed = useRef(false)
    const roomName = useRef('')

    const updateCallStatus = (callStatus: CallStatus) => {
        if (callStatus === activeAvailableCallRef.current.call?.status) {
            return
        }

        dispatch(
            upsertActiveAvailableCall({
                callId,
                call: {
                    ...activeAvailableCallRef.current.call,
                    status: callStatus,
                } as ClientCall,
            })
        )
    }

    const joinLivekitRoom = async (roomName: string) => {
        try {
            const response = await axios.post(`/voip/sipclient/livekit-room/${roomName}`)
            const { token, serverURL } = response.data.data
            const room = new Room()
            await room.connect(serverURL, token)
            livekitRoom.current = room

            // Subscribe to remote participant audio tracks
            room.remoteParticipants.forEach((participant) => {
                participant.audioTrackPublications.forEach((publication) => {
                    publication.track?.attach()
                    removeFakeRingtone()
                    updateCallStatus(CallStatus.CONNECTED)
                })
            })
            room.on(RoomEvent.TrackSubscribed, (track) => {
                if (track instanceof RemoteAudioTrack) {
                    track.attach()
                    removeFakeRingtone()
                    updateCallStatus(CallStatus.CONNECTED)
                }
            })

            // Subscribe to call status changes
            room.registerTextStreamHandler('CALL_STATUS_CHANGE', async (reader) => {
                const text = await reader.readAll()

                const receivedStatus = CallStatus[text.toUpperCase() as keyof typeof CallStatus]
                if (receivedStatus) {
                    updateCallStatus(receivedStatus)
                }

                if ([CallStatus.ENDED, CallStatus.BUSY].includes(text as CallStatus)) {
                    cleanUpOnCallEnded()
                }
            })

            await room.localParticipant.setMicrophoneEnabled(true)
        } catch (ex) {
            console.error('SoftPhoneCallWidget::joinLivekitRoom:: Failed to join livekit room', ex)
        }
    }

    const initializeCall = async () => {
        if (callInitialized.current || !activeAvailableCall?.load?._id) return
        callInitialized.current = true

        roomName.current = `manual|${application?.projectName}|${callId}`

        try {
            joinLivekitRoom(roomName.current)
            const response = await axios.post(
                '/voip/sipclient/make-plivo-call',
                {
                    callId,
                    phoneNumber: activeAvailableCall.phoneNumber,
                    roomName: roomName.current,
                },
                {
                    timeout: 30000,
                }
            )
            const { call } = response.data.data
            dispatch(upsertActiveAvailableCall({ callId, call: { ...call, status: activeAvailableCallRef.current.call?.status || CallStatus.INITIATING } }))
        } catch (error) {
            console.error('CallWidget::initializeAICall::', error)
            initializationFailed.current = true
            dispatch(removeCall(callId))

            switch ((error as { status: number }).status) {
                case 429:
                    dispatch(
                        addNotification({
                            id: '',
                            message: 'All available phone numbers are in use, please wait for a moment or connect your own phone number',
                            type: 'error',
                            category: Category.Error,
                            timestamp: Date.now(),
                            topic: 'temp',
                        })
                    )
                    break
                default:
                    dispatch(
                        addNotification({
                            id: '',
                            message: 'Failed to initiate the call. Please try again',
                            type: 'error',
                            category: Category.Error,
                            timestamp: Date.now(),
                            topic: 'temp',
                        })
                    )
            }
        }
    }

    const onToggleMute = async () => {
        const updatedMutedState = !muted
        setMuted(updatedMutedState)
        try {
            await livekitRoom.current?.localParticipant.setMicrophoneEnabled(!updatedMutedState)
        } catch (error) {
            console.error('CallWidget::onToggleMute:: Failed to toggle mute', error, { callId, updatedMutedState })
        }
    }

    const cleanUpOnCallEnded = async () => {
        try {
            await livekitRoom.current?.disconnect()
            if (!initializationFailed.current) {
                showError('ClientCall ended')
            }
        } catch (error) {
            console.error('CallWidget::onEndCall:: Failed to disconnect livekit room', error)
        }

        dispatch(removeCall(callId))
    }

    const onEndCall = async () => {
        setEndingCall(true)
        try {
            await waitFor({ condition: () => livekitRoom.current?.state === ConnectionState.Connected, interval: 1000, maxRetries: 5 })

            await livekitRoom.current?.localParticipant.sendText('hangup_call', { topic: 'hangup_call' })
        } catch (error) {
            console.error('CallWidget::onEndCall:: Failed to send hangup_call message', error)
        }
        await cleanUpOnCallEnded()
        setEndingCall(false)
    }

    const onMaximize = () => {
        setIsTransitioning(true)
        setTimeout(() => {
            setView(CallWidgetView.MAXIMIZED)
            setTimeout(() => setIsTransitioning(false), 300)
        }, 50)
    }

    const onMinimize = () => {
        setIsMinimizing(true)
        setTimeout(() => {
            setView(CallWidgetView.MINIMIZED)
            setIsMinimizing(false)
        }, 250)
    }

    const sendDTMF = async (digit: string) => {
        try {
            await livekitRoom.current?.localParticipant.sendText(digit, { topic: 'DTMF' })
        } catch (error) {
            console.error('CallWidget::sendDTMF:: Failed to send dtmf message', error)
        }
    }

    useEffect(() => {
        activeAvailableCallRef.current = activeAvailableCall
    }, [activeAvailableCall])

    useEffect(() => {
        initializeCall()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeAvailableCall?.load?._id])

    useEffect(
        () => () => {
            onEndCall()
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    )

    if (!activeAvailableCall) {
        return null
    }

    switch (view) {
        case CallWidgetView.MAXIMIZED:
            return (
                <MaximizedUserCallWidget
                    callId={callId}
                    onMinimize={onMinimize}
                    isMinimizing={isMinimizing}
                    muted={muted}
                    onToggleMute={onToggleMute}
                    onEndCall={onEndCall}
                    sendDTMF={sendDTMF}
                    endingCall={endingCall}
                />
            )
        case CallWidgetView.MINIMIZED:
            return (
                <MinimizedUserCallWidget
                    callId={callId}
                    onMaximize={onMaximize}
                    isTransitioning={isTransitioning}
                    onToggleMute={onToggleMute}
                    onEndCall={onEndCall}
                    muted={muted}
                    index={index}
                    endingCall={endingCall}
                />
            )
        default:
            return null
    }
}
